export const CARD_W = 132, CARD_H = 118, SP = 168, ROW = 196, TITLE_H = 64, GROUP_GAP = 72;

export const FAMILY_ROLES = [
  ["parent", "Parent"], ["child", "Child"], ["sibling", "Sibling"], ["half_sibling", "Half-sibling"],
  ["grandparent", "Grandparent"], ["grandchild", "Grandchild"], ["aunt_uncle", "Aunt/Uncle"], ["niece_nephew", "Niece/Nephew"],
  ["cousin", "Cousin"], ["adoptive_parent", "Adoptive parent"], ["adoptive_child", "Adoptive child"], ["other", "Other"],
];
export const roleLabel = (r) => (FAMILY_ROLES.find((x) => x[0] === r) || [null, "Other"])[1];

const fact = (p, s, k) => p.sections?.find((x) => x.title === s)?.facts.find((f) => f.key === k);
export const isDeceased = (p) => (fact(p, "Biographical Information", "Status")?.items || []).some((i) => /\b(deceased|dead)\b/i.test(i.text));

// Role is what T is to P (written on P's page)
function toRel(role, P, T) {
  const label = roleLabel(role).toLowerCase();
  switch (role) {
    case "parent": return { kind: "pc", up: T, down: P };
    case "adoptive_parent": return { kind: "pc", up: T, down: P, adoptive: true };
    case "child": return { kind: "pc", up: P, down: T };
    case "adoptive_child": return { kind: "pc", up: P, down: T, adoptive: true };
    case "sibling": return { kind: "sib", a: P, b: T };
    case "half_sibling": return { kind: "sib", a: P, b: T, half: true };
    case "grandparent": return { kind: "ext", up: T, down: P, dy: 2, label };
    case "grandchild": return { kind: "ext", up: P, down: T, dy: 2, label };
    case "aunt_uncle": return { kind: "ext", up: T, down: P, dy: 1, label };
    case "niece_nephew": return { kind: "ext", up: P, down: T, dy: 1, label };
    case "cousin": return { kind: "ext", up: P, down: T, dy: 0, label };
    default: return { kind: "other", a: P, b: T, label: "other" };
  }
}
const sig = (r) => (r.kind === "pc" || (r.kind === "ext" && r.dy) ? `${r.kind}:${r.up}>${r.down}` : r.kind);
export const ends = (e) => (e.up ? [e.up, e.down] : [e.a, e.b]);
const pairKey = (a, b) => [a, b].sort().join("|");

export function buildFamily(pages) {
  const chars = pages.filter((p) => p.category === "Characters");
  const byId = new Map(chars.map((p) => [p.id, p]));
  const recs = [], couples = [], missing = [];
  for (const P of chars) {
    for (const it of fact(P, "Relationships", "Family")?.items || []) {
      const T = byId.get(it.page_id);
      if (!T || T.id === P.id) continue;
      if (!it.role) missing.push({ page: P, target: T });
      recs.push({ ...toRel(it.role || "other", P.id, T.id), src: P, target: T.id, section: "Relationships", fact: "Family", role: it.role || "other" });
    }
    for (const it of fact(P, "Biographical Information", "Marital Status")?.items || []) {
      const T = byId.get(it.page_id);
      if (!T || T.id === P.id || couples.some((c) => pairKey(c.a, c.b) === pairKey(P.id, T.id))) continue;
      couples.push({ kind: "couple", a: P.id, b: T.id, note: it.note, src: P, target: T.id, section: "Biographical Information", fact: "Marital Status" });
    }
  }
  const groups = new Map();
  recs.forEach((r) => { const k = pairKey(r.src.id, r.target); groups.set(k, [...(groups.get(k) || []), r]); });
  const edges = [], conflicts = [];
  groups.forEach((rs) => {
    rs.sort((x, y) => y.src.updated_at.localeCompare(x.src.updated_at));
    if (new Set(rs.map(sig)).size > 1) conflicts.push({ pages: [...new Map(rs.map((r) => [r.src.id, r.src])).values()], winner: rs[0].src });
    edges.push(rs[0]);
  });
  return { chars, byId, edges, couples, missing, conflicts, records: [...recs, ...couples] };
}

export function focusSubset(fam, id) {
  const up = (c) => fam.edges.filter((e) => e.kind === "pc" && e.down === c).map((e) => e.up);
  const down = (c) => fam.edges.filter((e) => e.kind === "pc" && e.up === c).map((e) => e.down);
  const walk = (start, next, acc = new Set()) => { next(start).forEach((n) => { if (!acc.has(n)) { acc.add(n); walk(n, next, acc); } }); return acc; };
  const keep = new Set([id, ...walk(id, up), ...walk(id, down)]);
  fam.edges.filter((e) => e.kind === "sib" && (e.a === id || e.b === id)).forEach((e) => { keep.add(e.a); keep.add(e.b); });
  up(id).flatMap(down).forEach((s) => keep.add(s));
  fam.couples.filter((c) => c.a === id || c.b === id).forEach((c) => { keep.add(c.a); keep.add(c.b); });
  const inside = (e) => ends(e).every((x) => keep.has(x));
  return { ...fam, edges: fam.edges.filter(inside), couples: fam.couples.filter(inside), ids: [...keep] };
}

const groupTitle = (comp, byId) => {
  const counts = {};
  comp.forEach((id) => { const s = byId.get(id).title.trim().split(/\s+/).pop(); counts[s] = (counts[s] || 0) + 1; });
  const [best, n] = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return n >= 2 ? `The ${best} family` : `Family of ${byId.get(comp[0]).title}`;
};

function layoutComp(comp, links) {
  const adj = new Map(), soft = new Map(), nb = new Map(), parentsOf = new Map(), partners = new Map();
  const push = (m, k, v) => m.set(k, [...(m.get(k) || []), v]);
  for (const [a, b, e] of links) {
    push(nb, a, b); push(nb, b, a);
    if (e.kind === "other") { push(soft, a, b); push(soft, b, a); continue; }
    const vertical = e.kind === "pc" || e.kind === "ext";
    const d = e.kind === "pc" ? 1 : e.kind === "ext" ? e.dy : 0;
    const [u, v] = vertical ? [e.up, e.down] : [a, b];
    push(adj, u, { to: v, d }); push(adj, v, { to: u, d: -d });
    if (e.kind === "pc") push(parentsOf, e.down, e.up);
    if (e.kind === "couple") { push(partners, a, b); push(partners, b, a); }
  }
  const gen = new Map(), order = [];
  while (gen.size < comp.length) {
    let start = comp.find((id) => !gen.has(id) && (soft.get(id) || []).some((n) => gen.has(n)));
    const g0 = start ? gen.get(soft.get(start).find((n) => gen.has(n))) : 0;
    start = start || comp.find((id) => !gen.has(id));
    gen.set(start, g0);
    const q = [start];
    while (q.length) {
      const id = q.shift();
      order.push(id);
      for (const { to, d } of adj.get(id) || []) if (!gen.has(to)) { gen.set(to, gen.get(id) + d); q.push(to); }
    }
  }
  const minG = Math.min(...gen.values());
  const rows = [];
  order.forEach((id) => { const g = gen.get(id) - minG; (rows[g] = rows[g] || []).push(id); });
  const x = new Map();
  const avg = (ids) => ids.reduce((s, i) => s + x.get(i), 0) / ids.length;
  const desired = (id) => {
    const ps = (parentsOf.get(id) || []).filter((p) => x.has(p));
    if (ps.length) return avg(ps);
    const ns = (nb.get(id) || []).filter((p) => x.has(p));
    return ns.length ? avg(ns) : Infinity;
  };
  rows.forEach((row = []) => {
    const sorted = row.map((id, i) => ({ id, d: desired(id), i })).sort((a, b) => a.d - b.d || a.i - b.i);
    const final = [];
    sorted.forEach(({ id }) => {
      if (final.includes(id)) return;
      final.push(id);
      (partners.get(id) || []).filter((p) => row.includes(p) && !final.includes(p)).forEach((p) => final.push(p));
    });
    let prev = null;
    final.forEach((id) => {
      const d = desired(id);
      const xi = prev === null ? (Number.isFinite(d) ? d : 0) : Math.max(Number.isFinite(d) ? d : prev + SP, prev + SP);
      x.set(id, xi);
      prev = xi;
    });
  });
  const minX = Math.min(...x.values()), maxX = Math.max(...x.values());
  const local = new Map(comp.map((id) => [id, { x: x.get(id) - minX, y: (gen.get(id) - minG) * ROW }]));
  return { local, w: maxX - minX + CARD_W, h: (rows.length - 1) * ROW + CARD_H };
}

export function layoutFamily(ids, edges, couples, byId, titleOf = groupTitle) {
  const links = [...edges, ...couples].map((e) => [...ends(e), e]);
  const par = new Map(ids.map((i) => [i, i]));
  const find = (i) => (par.get(i) === i ? i : (par.set(i, find(par.get(i))), par.get(i)));
  links.forEach(([a, b]) => par.set(find(a), find(b)));
  const comps = new Map();
  [...ids].sort((a, b) => byId.get(a).title.localeCompare(byId.get(b).title)).forEach((id) => comps.set(find(id), [...(comps.get(find(id)) || []), id]));
  const pos = new Map(), groups = [];
  let y0 = 0, width = 0;
  [...comps.values()].sort((a, b) => b.length - a.length).forEach((comp) => {
    const set = new Set(comp);
    const { local, w, h } = layoutComp(comp, links.filter(([a, b]) => set.has(a) && set.has(b)));
    local.forEach((p, id) => pos.set(id, { x: p.x, y: p.y + y0 + TITLE_H }));
    groups.push({ title: titleOf(comp, byId), y: y0, w, h: h + TITLE_H, count: comp.length });
    width = Math.max(width, w);
    y0 += TITLE_H + h + GROUP_GAP;
  });
  return { pos, groups, width, height: Math.max(0, y0 - GROUP_GAP) };
}

export const familyIds = (fam) => [...new Set([...fam.edges, ...fam.couples].flatMap(ends))];
